import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import SuccessIcon from "shared/icons/status-icons/success-icon.svg?react";
import FailedIcon from "shared/icons/status-icons/fail-icon.svg?react";
import { FC, ReactNode } from "react";
import { useSettingsController } from "views/settings-integration/controller";
import { useAuthContext } from "providers/Auth";
import { PropertyManagementSystem } from "shared/types/partner";
import { PMSNames } from "views/settings-integration/integrations/pms/constants";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { PropertyKind } from "shared/types";

type Props = {
  isActive: boolean;
  isListed?: boolean;
  withText?: boolean;
  withListedInfo?: boolean;
  propertyKind?: PropertyKind | null;
  activeLabel?: ReactNode;
  inactiveLabel?: ReactNode;
  listedLabel?: ReactNode;
};

type ListedComponentProps = Pick<Props, "isListed" | "propertyKind"> & {
  label?: ReactNode;
};

const ListedComponent = ({
  isListed,
  propertyKind,
  label
}: ListedComponentProps) => {
  const dictionary = useUnitsPropertiesDictionary();

  const defaultLabel = `List ${
    propertyKind === PropertyKind.SingleFamily
      ? dictionary.property
      : dictionary.unit
  }`;

  return (
    <div>
      <div className="relative">
        <div className="absolute border-l border-content-100 left-2.5 h-[18px] -top-2" />
        <div className="absolute border-t border-content-100 left-2.5 top-[10px] w-[44px]" />
      </div>

      <div className="flex gap-2 place-items-center mt-3 ml-14">
        {isListed ? (
          <SuccessIcon className="w-5 text-primary-500" />
        ) : (
          <FailedIcon className="text-alpha-100 w-5" />
        )}

        <p className="text-content-1000 font-medium text-sm">
          {label ?? defaultLabel}
        </p>
      </div>
    </div>
  );
};

export const AllowOrDisallowApplications: FC<Props> = ({
  isActive,
  withText = true,
  isListed = false,
  withListedInfo = false,
  propertyKind,
  activeLabel,
  inactiveLabel,
  listedLabel
}) => {
  const { propertyManagementSystem } = useSettingsController();
  const { pms } = useAuthContext();

  return (
    <Tooltip>
      <TooltipTrigger>
        {isActive ? (
          <div className="flex gap-2 place-items-center">
            <SuccessIcon className="w-5 text-primary-500" />
            {withText ? (
              <p className="text-content-1000 font-medium text-sm">
                {activeLabel ?? "Allow applications"}
              </p>
            ) : null}
          </div>
        ) : (
          <div className="flex gap-2 place-items-center">
            <FailedIcon className="text-alpha-100 w-5" />
            {withText ? (
              <p className="text-content-1000 font-medium text-sm">
                {inactiveLabel ?? "Don't allow applications"}
              </p>
            ) : null}
          </div>
        )}
        {withListedInfo ? (
          <ListedComponent
            propertyKind={propertyKind}
            isListed={isListed}
            label={listedLabel}
          />
        ) : null}
      </TooltipTrigger>
      <TooltipContent className="max-w-[370px] rounded-lg z-[50] border bg-alpha-800 p-4 text-sm text-content-1000-inverted">
        {pms.isAppfolio
          ? 'You can allow/disallow applicants to submit applications by setting "Yes" or "No" under the "Marketing Information" section in your PMS.'
          : `Application submission status is automatically synchronized with ${PMSNames[propertyManagementSystem as PropertyManagementSystem]} listing status`}
      </TooltipContent>
    </Tooltip>
  );
};
